import { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, ChevronDown, ChevronUp, AlertCircle, X } from 'lucide-react';
import { useCallStore } from '@/store/callStore';
import { useRoomStore } from '@/store/roomStore';

interface RemoteAudioProps {
  stream: MediaStream;
}

function RemoteAudio({ stream }: RemoteAudioProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioRef.current && stream) {
      audioRef.current.srcObject = stream;
    }
  }, [stream]);

  return <audio ref={audioRef} autoPlay playsInline />;
}

interface VideoTileProps {
  stream: MediaStream;
  name: string;
  isLocal?: boolean;
  micEnabled?: boolean;
}

function VideoTile({ stream, name, isLocal, micEnabled }: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="relative w-36 sm:w-44 h-24 sm:h-28 bg-cinema-900 rounded-lg overflow-hidden border border-cinema-700/80 shadow-xl flex-shrink-0 group">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal} // Avoid local echo
        className={`w-full h-full object-cover ${isLocal ? 'scale-x-[-1]' : ''}`}
      />

      {/* Participant Name & Mic Tag */}
      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between pointer-events-none">
        <span className="text-[10px] font-medium text-white px-1.5 py-0.5 rounded bg-black/70 backdrop-blur-xs truncate max-w-[85%]">
          {name} {isLocal && '(You)'}
        </span>
        <span
          className={`p-1 rounded-full ${
            micEnabled ? 'bg-lamp/90 text-cinema-950' : 'bg-black/70 text-cinema-400'
          }`}
        >
          {micEnabled ? <Mic className="w-2.5 h-2.5" /> : <MicOff className="w-2.5 h-2.5" />}
        </span>
      </div>
    </div>
  );
}

export function FloatingVideoGrid() {
  const {
    localCamEnabled,
    localMicEnabled,
    localStream,
    remoteStreams,
    peerMediaStates,
    callError,
    setCallError,
  } = useCallStore();

  const { room, participantId, participantName } = useRoomStore();
  const [isMinimized, setIsMinimized] = useState(false);

  // Identify remote streams that have an active video track
  const activeRemoteVideos: Array<{ peerId: string; name: string; stream: MediaStream; micEnabled: boolean }> = [];

  for (const [peerId, stream] of Object.entries(remoteStreams)) {
    const hasVideo = stream.getVideoTracks().some((t) => t.readyState === 'live');
    const isCamOn = peerMediaStates[peerId]?.camEnabled ?? hasVideo;
    const isMicOn = peerMediaStates[peerId]?.micEnabled ?? false;
    const participant = room?.participants.find((p) => p.id === peerId);

    if (hasVideo && isCamOn) {
      activeRemoteVideos.push({
        peerId,
        name: participant?.name || 'Guest',
        stream,
        micEnabled: isMicOn,
      });
    }
  }

  const hasAnyVideo = localCamEnabled || activeRemoteVideos.length > 0;

  return (
    <>
      {/* ── Remote Audio Channels (Always active regardless of video) ── */}
      {Object.entries(remoteStreams).map(([peerId, stream]) => (
        <RemoteAudio key={`audio-${peerId}`} stream={stream} />
      ))}

      {/* ── Error Banner (if permission denied or device error) ── */}
      {callError && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-cinema-900 border border-amber-500/40 text-amber-200 px-4 py-2.5 rounded-lg shadow-2xl flex items-center gap-3 text-xs animate-fade-in max-w-md w-full mx-4">
          <AlertCircle className="w-4 h-4 text-lamp flex-shrink-0" />
          <p className="flex-1 leading-relaxed">{callError}</p>
          <button
            onClick={() => setCallError(null)}
            className="p-1 rounded text-cinema-400 hover:text-cinema-100 transition-colors"
            aria-label="Dismiss error"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Floating Video Tiles: Render ONLY when at least one camera is active ── */}
      {hasAnyVideo && (
        <div className="absolute bottom-20 right-4 z-30 flex flex-col items-end gap-2 pointer-events-auto">
          {/* Collapse/Expand Toggle Header */}
          <div className="flex items-center gap-1.5 bg-cinema-900/90 border border-cinema-700/80 px-2.5 py-1 rounded-md text-[11px] text-cinema-300 shadow-md backdrop-blur-sm">
            <span className="font-semibold text-cinema-100">Video Chat</span>
            <span className="text-cinema-400 font-mono">
              ({(localCamEnabled ? 1 : 0) + activeRemoteVideos.length})
            </span>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-0.5 rounded text-cinema-400 hover:text-cinema-100 transition-colors ml-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp"
              title={isMinimized ? 'Expand video tiles' : 'Minimize video tiles'}
            >
              {isMinimized ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Video Grid Tiles */}
          {!isMinimized && (
            <div className="flex flex-row-reverse sm:flex-col gap-2 max-h-72 overflow-x-auto sm:overflow-y-auto p-1 scrollbar-none animate-fade-in">
              {/* Local Camera Tile */}
              {localCamEnabled && localStream && (
                <VideoTile
                  stream={localStream}
                  name={participantName || 'You'}
                  isLocal={true}
                  micEnabled={localMicEnabled}
                />
              )}

              {/* Remote Participants' Camera Tiles */}
              {activeRemoteVideos.map((rv) => (
                <VideoTile
                  key={`video-${rv.peerId}`}
                  stream={rv.stream}
                  name={rv.name}
                  isLocal={false}
                  micEnabled={rv.micEnabled}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

